import AmbientGlow from '../components/AmbientGlow';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  Shield, Users, Music, MessageSquare, TrendingUp, DollarSign, 
  Settings, UserX, Trash2, Eye, ChevronRight, Search, Filter,
  UserPlus, AlertTriangle, CheckCircle, XCircle, BarChart3,
  Activity, Crown, Loader2, RefreshCw, Download, Plus
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OwnerDashboardPage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [isOwner, setIsOwner] = useState(false);
  
  // Data states
  const [overview, setOverview] = useState(null);
  const [users, setUsers] = useState([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersPage, setUsersPage] = useState(1);
  const [userSearch, setUserSearch] = useState('');
  const [userFilter, setUserFilter] = useState('all'); // all, artist, listener, suspended
  
  const [staff, setStaff] = useState([]);
  const [artists, setArtists] = useState([]);
  const [artistsPage, setArtistsPage] = useState(1);
  const [artistsTotal, setArtistsTotal] = useState(0);
  
  const [votes, setVotes] = useState([]);
  const [votesPage, setVotesPage] = useState(1);
  const [votesTotal, setVotesTotal] = useState(0);
  
  const [activity, setActivity] = useState([]);
  const [userGrowth, setUserGrowth] = useState([]);
  
  const [financials, setFinancials] = useState(null);
  const [settings, setSettings] = useState(null);
  
  const [actionLog, setActionLog] = useState([]);
  
  // Modal states
  const [showStaffModal, setShowStaffModal] = useState(false);
  const [showRevenueModal, setShowRevenueModal] = useState(false);
  const [newStaffEmail, setNewStaffEmail] = useState('');
  const [newStaffScopes, setNewStaffScopes] = useState([]);
  const [newRevenue, setNewRevenue] = useState({ amount: '', type: 'streaming', description: '' });

  // Check owner status
  useEffect(() => {
    const checkOwner = async () => {
      if (!token) {
        navigate('/auth');
        return;
      }
      
      try {
        const res = await fetch(`${API_URL}/api/admin/check-owner`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await res.json();
        
        if (!data.is_owner) {
          navigate('/');
          return;
        }
        
        setIsOwner(true);
        setLoading(false);
      } catch (err) {
        console.error('Error checking owner status:', err);
        navigate('/');
      }
    };
    
    checkOwner();
  }, [token, navigate]);

  // Fetch data based on active tab
  const fetchData = useCallback(async () => {
    if (!isOwner || !token) return;
    
    try {
      setLoading(true);
      
      if (activeTab === 'overview') {
        const [overviewRes, activityRes, growthRes] = await Promise.all([
          fetch(`${API_URL}/api/admin/analytics/overview`, { headers: { 'Authorization': `Bearer ${token}` }}),
          fetch(`${API_URL}/api/admin/analytics/activity?days=7`, { headers: { 'Authorization': `Bearer ${token}` }}),
          fetch(`${API_URL}/api/admin/analytics/users?days=30`, { headers: { 'Authorization': `Bearer ${token}` }})
        ]);
        
        setOverview(await overviewRes.json());
        setActivity((await activityRes.json()).activity || []);
        setUserGrowth((await growthRes.json()).growth_data || []);
      }
      
      if (activeTab === 'users') {
        const params = new URLSearchParams({ page: usersPage, limit: 50 });
        if (userSearch) params.append('search', userSearch);
        if (userFilter === 'suspended') params.append('suspended', 'true');
        if (userFilter === 'artist' || userFilter === 'listener') params.append('role', userFilter);
        
        const res = await fetch(`${API_URL}/api/admin/users?${params}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await res.json();
        setUsers(data.users || []);
        setUsersTotal(data.total || 0);
      }
      
      if (activeTab === 'staff') {
        const res = await fetch(`${API_URL}/api/admin/staff`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await res.json();
        setStaff(data.staff || []);
      }
      
      if (activeTab === 'artists') {
        const res = await fetch(`${API_URL}/api/admin/analytics/artists?page=${artistsPage}&limit=50`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await res.json();
        setArtists(data.artists || []);
        setArtistsTotal(data.total || 0);
      }
      
      if (activeTab === 'votes') {
        const res = await fetch(`${API_URL}/api/admin/analytics/votes?page=${votesPage}&limit=50`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await res.json();
        setVotes(data.tracks || []);
        setVotesTotal(data.total || 0);
      }
      
      if (activeTab === 'financials') {
        const res = await fetch(`${API_URL}/api/admin/financials?days=30`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setFinancials(await res.json());
      }
      
      if (activeTab === 'settings') {
        const res = await fetch(`${API_URL}/api/admin/settings`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setSettings(await res.json());
      }
      
      if (activeTab === 'logs') {
        const res = await fetch(`${API_URL}/api/admin/action-log?limit=100`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await res.json();
        setActionLog(data.actions || []);
      }
      
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  }, [isOwner, token, activeTab, usersPage, userSearch, userFilter, artistsPage, votesPage]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Actions
  const suspendUser = async (userId) => {
    if (!window.confirm('Are you sure you want to suspend this user?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/admin/users/${userId}/suspend`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason: 'Suspended by owner' })
      });
      
      if (res.ok) {
        fetchData();
      }
    } catch (err) {
      console.error('Error suspending user:', err);
    }
  };

  const unsuspendUser = async (userId) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/users/${userId}/unsuspend`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        fetchData();
      }
    } catch (err) {
      console.error('Error unsuspending user:', err);
    }
  };

  const deleteTrack = async (trackId, trackTitle) => {
    if (!window.confirm(`Delete track "${trackTitle}"? This cannot be undone.`)) return;
    
    try {
      await fetch(`${API_URL}/api/admin/tracks/${trackId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchData();
    } catch (err) {
      console.error('Error deleting track:', err);
    }
  };

  const createStaff = async () => {
    if (!newStaffEmail || newStaffScopes.length === 0) return;
    
    try {
      const res = await fetch(`${API_URL}/api/admin/staff`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ user_email: newStaffEmail, scopes: newStaffScopes })
      });
      
      if (res.ok) {
        setShowStaffModal(false);
        setNewStaffEmail('');
        setNewStaffScopes([]);
        fetchData();
      } else {
        const err = await res.json();
        alert(err.detail || 'Failed to create staff');
      }
    } catch (err) {
      console.error('Error creating staff:', err);
    }
  };

  const removeStaff = async (staffId) => {
    if (!window.confirm('Remove this staff member?')) return;
    
    try {
      await fetch(`${API_URL}/api/admin/staff/${staffId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchData();
    } catch (err) {
      console.error('Error removing staff:', err);
    }
  };

  const addRevenueEntry = async () => {
    if (!newRevenue.amount || !newRevenue.description) return;
    
    try {
      const res = await fetch(`${API_URL}/api/admin/financials/entry`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          amount: parseFloat(newRevenue.amount),
          type: newRevenue.type,
          description: newRevenue.description
        })
      });
      
      if (res.ok) {
        setShowRevenueModal(false);
        setNewRevenue({ amount: '', type: 'streaming', description: '' });
        fetchData();
      }
    } catch (err) {
      console.error('Error adding revenue:', err);
    }
  };

  const updateSettings = async (key, value) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/settings`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ [key]: value })
      });
      
      if (res.ok) {
        fetchData();
      }
    } catch (err) {
      console.error('Error updating settings:', err);
    }
  };

  const setupOfficialAccount = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/setup-official-account`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      alert(data.message);
    } catch (err) {
      console.error('Error setting up official account:', err);
    }
  };

  if (!isOwner) {
    return (
      <div className="min-h-screen bg-[#050507] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-purple-500 animate-spin" />
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'artists', label: 'Artists', icon: Music },
    { id: 'votes', label: 'Votes', icon: TrendingUp },
    { id: 'staff', label: 'Staff', icon: Shield },
    { id: 'financials', label: 'Financials', icon: DollarSign },
    { id: 'settings', label: 'Settings', icon: Settings },
    { id: 'logs', label: 'Action Log', icon: Activity },
  ];

  return (
    <div className="min-h-screen bg-[#050507] text-white pb-24">
      <AmbientGlow />
      {/* Header */}
      <div className="relative bg-gradient-to-r from-[#3b0a52] via-[#4a1466] to-[#1a1046] p-6 border-b border-white/10">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-3 bg-[#B14BFF]/25 rounded-2xl shadow-lg">
            <Crown className="w-8 h-8 text-[#FFD700]" />
          </div>
          <div>
            <h1 className="text-3xl font-black tracking-tight" style={{ fontFamily: 'Unbounded, sans-serif' }}>Owner Dashboard</h1>
            <p className="text-purple-200 text-sm">Full platform administration</p>
          </div>
          <button
            onClick={() => navigate('/owner/traffic')}
            data-testid="open-traffic-btn"
            className="ml-auto flex items-center gap-2 px-4 py-2 rounded-lg bg-white/15 hover:bg-white/25 transition-colors font-medium text-sm"
          >
            <TrendingUp className="w-4 h-4" /> Traffic & Visitors
          </button>
        </div>
        
        {/* Tab Navigation */}
        <div className="flex gap-2 overflow-x-auto pb-2 -mx-2 px-2">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap transition-all ${
                activeTab === tab.id 
                  ? 'bg-white text-purple-900 font-semibold' 
                  : 'bg-purple-700/50 text-white hover:bg-purple-600/50'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="p-4">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-purple-500 animate-spin" />
          </div>
        ) : (
          <>
            {/* Overview Tab */}
            {activeTab === 'overview' && overview && (
              <div className="space-y-6">
                {/* Stats Grid */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gradient-to-br from-blue-600 to-blue-800 rounded-xl p-4">
                    <Users className="w-6 h-6 mb-2 text-blue-200" />
                    <div className="text-3xl font-bold">{overview.users?.total || 0}</div>
                    <div className="text-blue-200 text-sm">Total Users</div>
                    <div className="text-xs text-blue-300 mt-1">+{overview.users?.new_this_week || 0} this week</div>
                  </div>
                  
                  <div className="bg-gradient-to-br from-purple-600 to-purple-800 rounded-xl p-4">
                    <Music className="w-6 h-6 mb-2 text-purple-200" />
                    <div className="text-3xl font-bold">{overview.users?.artists || 0}</div>
                    <div className="text-purple-200 text-sm">Artists</div>
                    <div className="text-xs text-purple-300 mt-1">{overview.users?.listeners || 0} listeners</div>
                  </div>
                  
                  <div className="bg-gradient-to-br from-green-600 to-green-800 rounded-xl p-4">
                    <TrendingUp className="w-6 h-6 mb-2 text-green-200" />
                    <div className="text-3xl font-bold">{(overview.content?.total_plays || 0).toLocaleString()}</div>
                    <div className="text-green-200 text-sm">Total Plays</div>
                    <div className="text-xs text-green-300 mt-1">{overview.content?.total_tracks || 0} tracks</div>
                  </div>
                  
                  <div className="bg-gradient-to-br from-orange-600 to-orange-800 rounded-xl p-4">
                    <MessageSquare className="w-6 h-6 mb-2 text-orange-200" />
                    <div className="text-3xl font-bold">{overview.content?.messages_this_week || 0}</div>
                    <div className="text-orange-200 text-sm">Messages (7d)</div>
                    <div className="text-xs text-orange-300 mt-1">{overview.content?.total_votes || 0} votes</div>
                  </div>
                </div>

                {/* Activity Chart */}
                <div className="bg-white/[0.04] border border-white/10 rounded-xl p-4">
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <Activity className="w-5 h-5 text-purple-400" />
                    Platform Activity (7 Days)
                  </h3>
                  <div className="space-y-3">
                    {activity.slice(-7).map((day, i) => (
                      <div key={i} className="flex items-center gap-4 text-sm">
                        <span className="w-20 text-gray-400">{day.date?.slice(5) || '-'}</span>
                        <div className="flex-1 flex gap-2">
                          <div className="bg-blue-500/30 px-2 py-1 rounded text-xs">
                            {day.plays || 0} plays
                          </div>
                          <div className="bg-green-500/30 px-2 py-1 rounded text-xs">
                            {day.votes || 0} votes
                          </div>
                          <div className="bg-purple-500/30 px-2 py-1 rounded text-xs">
                            {day.chat_messages || 0} msgs
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* User Growth */}
                <div className="bg-white/[0.04] border border-white/10 rounded-xl p-4">
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-green-400" />
                    User Growth (30 Days)
                  </h3>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <div className="text-2xl font-bold text-blue-400">
                        {userGrowth.reduce((sum, d) => sum + (d.total || 0), 0)}
                      </div>
                      <div className="text-xs text-gray-400">New Users</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-purple-400">
                        {userGrowth.reduce((sum, d) => sum + (d.artists || 0), 0)}
                      </div>
                      <div className="text-xs text-gray-400">New Artists</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-green-400">
                        {userGrowth.reduce((sum, d) => sum + (d.listeners || 0), 0)}
                      </div>
                      <div className="text-xs text-gray-400">New Listeners</div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Users Tab */}
            {activeTab === 'users' && (
              <div className="space-y-4">
                {/* Search & Filter */}
                <div className="flex gap-2">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search users..."
                      value={userSearch}
                      onChange={(e) => setUserSearch(e.target.value)}
                      className="w-full bg-white/[0.04] border border-white/10 rounded-lg pl-10 pr-4 py-2 text-sm"
                    />
                  </div>
                  <select
                    value={userFilter}
                    onChange={(e) => setUserFilter(e.target.value)}
                    className="bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-sm"
                  >
                    <option value="all">All Users</option>
                    <option value="artist">Artists</option>
                    <option value="listener">Listeners</option>
                    <option value="suspended">Suspended</option>
                  </select>
                  <button onClick={fetchData} className="p-2 bg-white/[0.04] border border-white/10 rounded-lg">
                    <RefreshCw className="w-4 h-4" />
                  </button>
                </div>

                <div className="text-sm text-gray-400">{usersTotal} users total</div>

                {/* Users List */}
                <div className="space-y-2">
                  {users.map(u => (
                    <div key={u.id} className="bg-white/[0.04] border border-white/10 rounded-lg p-3 flex items-center gap-3">
                      <div className="w-10 h-10 bg-white/[0.07] rounded-full flex items-center justify-center">
                        {u.profilePhoto ? (
                          <img src={u.profilePhoto} alt="" className="w-10 h-10 rounded-full object-cover" />
                        ) : (
                          <Users className="w-5 h-5 text-gray-400" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{u.name || u.username}</div>
                        <div className="text-xs text-gray-400 truncate">{u.email}</div>
                        <div className="flex gap-2 mt-1">
                          {u.is_artist && (
                            <span className="text-xs bg-purple-500/30 text-purple-300 px-2 py-0.5 rounded">
                              Artist
                            </span>
                          )}
                          {u.isSuspended && (
                            <span className="text-xs bg-red-500/30 text-red-300 px-2 py-0.5 rounded">
                              Suspended
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {u.isSuspended ? (
                          <button
                            onClick={() => unsuspendUser(u.id)}
                            className="p-2 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30"
                            title="Unsuspend"
                          >
                            <CheckCircle className="w-4 h-4" />
                          </button>
                        ) : (
                          <button
                            onClick={() => suspendUser(u.id)}
                            className="p-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30"
                            title="Suspend"
                          >
                            <UserX className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pagination */}
                {usersTotal > 50 && (
                  <div className="flex justify-center gap-2">
                    <button
                      onClick={() => setUsersPage(p => Math.max(1, p - 1))}
                      disabled={usersPage === 1}
                      className="px-4 py-2 bg-white/[0.04] border border-white/10 rounded-lg disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <span className="px-4 py-2">Page {usersPage}</span>
                    <button
                      onClick={() => setUsersPage(p => p + 1)}
                      disabled={users.length < 50}
                      className="px-4 py-2 bg-white/[0.04] border border-white/10 rounded-lg disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Artists Tab */}
            {activeTab === 'artists' && (
              <div className="space-y-4">
                <div className="text-sm text-gray-400">{artistsTotal} artists total</div>
                
                <div className="space-y-2">
                  {artists.map(a => (
                    <div key={a.id} className="bg-white/[0.04] border border-white/10 rounded-lg p-4">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-lg font-bold">
                          {a.stage_name?.charAt(0) || '?'}
                        </div>
                        <div className="flex-1">
                          <div className="font-semibold">{a.stage_name}</div>
                          <div className="text-xs text-gray-400">{a.genres?.join(', ')}</div>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-4 gap-2 text-center text-sm">
                        <div className="bg-white/[0.07]/50 rounded-lg p-2">
                          <div className="font-bold text-blue-400">{(a.total_plays || 0).toLocaleString()}</div>
                          <div className="text-xs text-gray-400">Plays</div>
                        </div>
                        <div className="bg-white/[0.07]/50 rounded-lg p-2">
                          <div className="font-bold text-purple-400">{a.track_count || 0}</div>
                          <div className="text-xs text-gray-400">Tracks</div>
                        </div>
                        <div className="bg-white/[0.07]/50 rounded-lg p-2">
                          <div className="font-bold text-green-400">{a.follower_count || 0}</div>
                          <div className="text-xs text-gray-400">Followers</div>
                        </div>
                        <div className="bg-white/[0.07]/50 rounded-lg p-2">
                          <div className={`font-bold ${a.net_score >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                            {a.net_score || 0}
                          </div>
                          <div className="text-xs text-gray-400">Net Score</div>
                        </div>
                      </div>

                      {/* Vote Breakdown */}
                      <div className="mt-3 flex gap-4 text-xs">
                        <span className="text-orange-400">🔥 {a.votes?.issa_hit || 0} Hits</span>
                        <span className="text-red-400">👎 {a.votes?.flop || 0} Flops</span>
                        <span className="text-blue-400">🎸 {a.votes?.rocking_with_it || 0} Rocking</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Votes Tab */}
            {activeTab === 'votes' && (
              <div className="space-y-4">
                <div className="text-sm text-gray-400">{votesTotal} tracks with votes</div>
                
                <div className="space-y-2">
                  {votes.map(track => (
                    <div key={track.id} className="bg-white/[0.04] border border-white/10 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <div className="font-semibold">{track.track_title}</div>
                          <div className="text-sm text-gray-400">by {track.artist_name || 'Unknown'}</div>
                        </div>
                        <button
                          onClick={() => deleteTrack(track.id, track.track_title)}
                          className="p-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30"
                          title="Delete Track"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      
                      <div className="grid grid-cols-4 gap-2 text-center text-sm">
                        <div className="bg-orange-500/20 rounded-lg p-2">
                          <div className="font-bold text-orange-400">{track.issa_hit || 0}</div>
                          <div className="text-xs text-gray-400">🔥 Issa Hit</div>
                        </div>
                        <div className="bg-red-500/20 rounded-lg p-2">
                          <div className="font-bold text-red-400">{track.flop || 0}</div>
                          <div className="text-xs text-gray-400">👎 Flop</div>
                        </div>
                        <div className="bg-blue-500/20 rounded-lg p-2">
                          <div className="font-bold text-blue-400">{track.rocking_with_it || 0}</div>
                          <div className="text-xs text-gray-400">🎸 Rocking</div>
                        </div>
                        <div className={`${track.net_score >= 0 ? 'bg-green-500/20' : 'bg-red-500/20'} rounded-lg p-2`}>
                          <div className={`font-bold ${track.net_score >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                            {track.net_score || 0}
                          </div>
                          <div className="text-xs text-gray-400">Net Score</div>
                        </div>
                      </div>
                      
                      <div className="mt-2 text-xs text-gray-400">
                        {(track.play_count || 0).toLocaleString()} plays • {track.total_votes || 0} total votes
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Staff Tab */}
            {activeTab === 'staff' && (
              <div className="space-y-4">
                <button
                  onClick={() => setShowStaffModal(true)}
                  className="w-full bg-purple-600 hover:bg-purple-700 rounded-lg p-3 flex items-center justify-center gap-2"
                >
                  <UserPlus className="w-5 h-5" />
                  Add Staff Member
                </button>

                <div className="space-y-2">
                  {staff.length === 0 ? (
                    <div className="text-center py-8 text-gray-400">
                      No staff members assigned yet
                    </div>
                  ) : (
                    staff.map(s => (
                      <div key={s.id} className="bg-white/[0.04] border border-white/10 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <div className="font-semibold">{s.user?.name || s.user_email}</div>
                            <div className="text-sm text-gray-400">{s.user_email}</div>
                          </div>
                          <button
                            onClick={() => removeStaff(s.id)}
                            className="p-2 bg-red-500/20 text-red-400 rounded-lg"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {s.scopes?.map(scope => (
                            <span key={scope} className="text-xs bg-purple-500/30 text-purple-300 px-2 py-1 rounded">
                              {scope.replace('_', ' ')}
                            </span>
                          ))}
                        </div>
                        {!s.active && (
                          <div className="mt-2 text-xs text-red-400">Inactive</div>
                        )}
                      </div>
                    ))
                  )}
                </div>

                <div className="mt-6">
                  <h3 className="font-semibold mb-3">Auto-Follow Accounts</h3>
                  <button
                    onClick={setupOfficialAccount}
                    className="w-full bg-white/[0.04] border border-white/10 hover:bg-white/[0.07] rounded-lg p-3 flex items-center justify-center gap-2"
                  >
                    <Shield className="w-5 h-5 text-blue-400" />
                    Setup NLT Radio Official Account
                  </button>
                  <p className="text-xs text-gray-400 mt-2">
                    New users will automatically follow the owner and official accounts
                  </p>
                </div>
              </div>
            )}

            {/* Financials Tab */}
            {activeTab === 'financials' && financials && (
              <div className="space-y-4">
                <div className="bg-gradient-to-br from-green-600 to-green-800 rounded-xl p-4">
                  <div className="text-sm text-green-200">Total Revenue (30 days)</div>
                  <div className="text-3xl font-bold">${(financials.total_revenue || 0).toFixed(2)}</div>
                  <div className="text-xs text-green-300 mt-1">Platform fee: {financials.platform_fee_percentage}%</div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  {Object.entries(financials.totals || {}).map(([type, amount]) => (
                    <div key={type} className="bg-white/[0.04] border border-white/10 rounded-lg p-3">
                      <div className="text-sm text-gray-400 capitalize">{type.replace('_', ' ')}</div>
                      <div className="text-xl font-bold">${(amount || 0).toFixed(2)}</div>
                    </div>
                  ))}
                </div>

                <button
                  onClick={() => setShowRevenueModal(true)}
                  className="w-full bg-green-600 hover:bg-green-700 rounded-lg p-3 flex items-center justify-center gap-2"
                >
                  <Plus className="w-5 h-5" />
                  Add Revenue Entry
                </button>

                <div className="bg-yellow-500/20 border border-yellow-500/30 rounded-lg p-3 text-sm text-yellow-300">
                  <AlertTriangle className="w-4 h-4 inline mr-2" />
                  Manual tracking mode. Will be enhanced with Stripe Connect in Step 11.
                </div>

                <h3 className="font-semibold mt-4">Recent Entries</h3>
                <div className="space-y-2">
                  {(financials.recent_entries || []).slice(0, 10).map(entry => (
                    <div key={entry.id} className="bg-white/[0.04] border border-white/10 rounded-lg p-3 flex items-center justify-between">
                      <div>
                        <div className="font-medium">{entry.description}</div>
                        <div className="text-xs text-gray-400 capitalize">{entry.type} • {entry.created_at?.slice(0, 10)}</div>
                      </div>
                      <div className="text-green-400 font-semibold">${entry.amount?.toFixed(2)}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Settings Tab */}
            {activeTab === 'settings' && settings && (
              <div className="space-y-4">
                <div className="bg-white/[0.04] border border-white/10 rounded-lg p-4">
                  <h3 className="font-semibold mb-4">Platform Settings</h3>
                  
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium">Platform Fee</div>
                        <div className="text-xs text-gray-400">Percentage taken from transactions</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          value={settings.platform_fee_percentage || 15}
                          onChange={(e) => updateSettings('platform_fee_percentage', parseFloat(e.target.value))}
                          className="w-20 bg-white/[0.07] rounded px-3 py-1 text-right"
                          min="0"
                          max="50"
                        />
                        <span>%</span>
                      </div>
                    </div>

                    {[
                      { key: 'auto_follow_enabled', label: 'Auto-Follow', desc: 'New users auto-follow owner & official' },
                      { key: 'chat_enabled', label: 'Chat', desc: 'Enable platform-wide chat' },
                      { key: 'voting_enabled', label: 'Voting', desc: 'Enable track voting' },
                      { key: 'store_enabled', label: 'Store', desc: 'Enable store/purchases' },
                      { key: 'new_uploads_require_review', label: 'Review Uploads', desc: 'Require review for new tracks' },
                    ].map(setting => (
                      <div key={setting.key} className="flex items-center justify-between">
                        <div>
                          <div className="font-medium">{setting.label}</div>
                          <div className="text-xs text-gray-400">{setting.desc}</div>
                        </div>
                        <button
                          onClick={() => updateSettings(setting.key, !settings[setting.key])}
                          className={`w-12 h-6 rounded-full transition-colors ${
                            settings[setting.key] ? 'bg-green-500' : 'bg-gray-600'
                          }`}
                        >
                          <div className={`w-5 h-5 bg-white rounded-full transition-transform ${
                            settings[setting.key] ? 'translate-x-6' : 'translate-x-0.5'
                          }`} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Action Log Tab */}
            {activeTab === 'logs' && (
              <div className="space-y-2">
                {actionLog.length === 0 ? (
                  <div className="text-center py-8 text-gray-400">No actions logged yet</div>
                ) : (
                  actionLog.map((action, i) => (
                    <div key={i} className="bg-white/[0.04] border border-white/10 rounded-lg p-3 text-sm">
                      <div className="flex items-center justify-between">
                        <span className="font-medium capitalize">{action.action?.replace(/_/g, ' ')}</span>
                        <span className="text-xs text-gray-400">{action.timestamp?.slice(0, 16)}</span>
                      </div>
                      {action.target_user_id && (
                        <div className="text-xs text-gray-400">User: {action.target_user_id}</div>
                      )}
                      {action.track_title && (
                        <div className="text-xs text-gray-400">Track: {action.track_title}</div>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Add Staff Modal */}
      {showStaffModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-white/[0.04] border border-white/10 rounded-xl p-6 w-full max-w-md">
            <h3 className="text-xl font-bold mb-4">Add Staff Member</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">User Email</label>
                <input
                  type="email"
                  value={newStaffEmail}
                  onChange={(e) => setNewStaffEmail(e.target.value)}
                  className="w-full bg-white/[0.07] rounded-lg px-4 py-2"
                  placeholder="user@example.com"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">Permission Scopes</label>
                {['chat_moderator', 'content_moderator', 'user_moderator', 'full_moderator'].map(scope => (
                  <label key={scope} className="flex items-center gap-2 mb-2">
                    <input
                      type="checkbox"
                      checked={newStaffScopes.includes(scope)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setNewStaffScopes([...newStaffScopes, scope]);
                        } else {
                          setNewStaffScopes(newStaffScopes.filter(s => s !== scope));
                        }
                      }}
                      className="rounded"
                    />
                    <span className="capitalize">{scope.replace(/_/g, ' ')}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowStaffModal(false)}
                className="flex-1 bg-white/[0.07] hover:bg-gray-600 rounded-lg py-2"
              >
                Cancel
              </button>
              <button
                onClick={createStaff}
                className="flex-1 bg-purple-600 hover:bg-purple-700 rounded-lg py-2"
              >
                Add Staff
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Revenue Modal */}
      {showRevenueModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-white/[0.04] border border-white/10 rounded-xl p-6 w-full max-w-md">
            <h3 className="text-xl font-bold mb-4">Add Revenue Entry</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Amount ($)</label>
                <input
                  type="number"
                  value={newRevenue.amount}
                  onChange={(e) => setNewRevenue({...newRevenue, amount: e.target.value})}
                  className="w-full bg-white/[0.07] rounded-lg px-4 py-2"
                  placeholder="0.00"
                  step="0.01"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">Type</label>
                <select
                  value={newRevenue.type}
                  onChange={(e) => setNewRevenue({...newRevenue, type: e.target.value})}
                  className="w-full bg-white/[0.07] rounded-lg px-4 py-2"
                >
                  <option value="streaming">Streaming</option>
                  <option value="tips">Tips & Donations</option>
                  <option value="sales">Sales</option>
                  <option value="platform_fee">Platform Fee</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">Description</label>
                <input
                  type="text"
                  value={newRevenue.description}
                  onChange={(e) => setNewRevenue({...newRevenue, description: e.target.value})}
                  className="w-full bg-white/[0.07] rounded-lg px-4 py-2"
                  placeholder="Description..."
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowRevenueModal(false)}
                className="flex-1 bg-white/[0.07] hover:bg-gray-600 rounded-lg py-2"
              >
                Cancel
              </button>
              <button
                onClick={addRevenueEntry}
                className="flex-1 bg-green-600 hover:bg-green-700 rounded-lg py-2"
              >
                Add Entry
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OwnerDashboardPage;
